
import React, { useState, useEffect } from 'react';
import { ref, onValue, update, remove } from 'firebase/database';
import { db } from '../../firebase';
import { SupportRequest } from '../../types';
import { Headphones, Clock, CheckCircle2, Trash2, Mail, User, MessageSquare, Search, Filter, Phone } from 'lucide-react';

const AdminSupport: React.FC = () => {
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'open' | 'closed'>('open');

  useEffect(() => {
    const supportRef = ref(db, 'support_requests');
    onValue(supportRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setRequests((Object.values(data) as SupportRequest[]).sort((a: SupportRequest, b: SupportRequest) => b.createdAt - a.createdAt));
      }
    });
  }, []);

  const updateStatus = async (id: string, status: 'open' | 'closed') => {
    await update(ref(db, `support_requests/${id}`), { status });
  };

  const deleteRequest = async (id: string) => {
    if (window.confirm('Bu talebi silmek istediğinize emin misiniz?')) {
      await remove(ref(db, `support_requests/${id}`));
    }
  };

  const filteredRequests = requests.filter(r => {
    const matchesSearch = r.userName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          r.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          r.message.toLowerCase().includes(searchTerm.toLowerCase());
    if (filter === 'all') return matchesSearch;
    return matchesSearch && r.status === filter;
  });

  const getSubjectLabel = (subject: string) => {
    switch(subject) {
      case 'return': return 'İade / Değişim';
      case 'complaint': return 'Şikayet';
      case 'satisfaction': return 'Memnuniyet';
      default: return 'Diğer';
    }
  };

  return (
    <div className="p-8">
      <div className="flex flex-col md:flex-row items-center justify-between mb-10 gap-6">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Destek Talepleri</h1>
          <p className="text-stone-500 font-medium italic mt-1">Müşterilerden gelen tüm mesajlar ve talepler.</p>
        </div>

        <div className="flex bg-white p-1.5 rounded-2xl border border-stone-100 shadow-sm">
          {[
            { id: 'open', label: 'Açık' },
            { id: 'closed', label: 'Çözüldü' },
            { id: 'all', label: 'Tümü' },
          ].map((f) => (
            <button 
              key={f.id}
              onClick={() => setFilter(f.id as any)}
              className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${filter === f.id ? 'bg-amber-800 text-white' : 'text-stone-400'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-72">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
          <input 
            type="text"
            placeholder="Talep veya müşteri ara..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-white border border-stone-100 rounded-2xl py-3 pl-12 pr-4 focus:ring-2 focus:ring-amber-500 outline-none text-sm font-medium"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {filteredRequests.length > 0 ? filteredRequests.map((req) => (
          <div key={req.id} className={`bg-white p-8 rounded-[2.5rem] border ${req.status === 'open' ? 'border-amber-100' : 'border-stone-50'} shadow-sm group hover:shadow-xl transition-all`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-stone-50 rounded-2xl flex items-center justify-center text-amber-800 border border-stone-100 shadow-inner">
                  <User size={24} />
                </div>
                <div>
                  <h3 className="font-black text-stone-900 text-lg">{req.userName}</h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-400 font-bold">
                    <span className="flex items-center"><Mail size={12} className="mr-1" /> {req.userEmail}</span>
                    <span className="flex items-center text-amber-800 font-black"><Phone size={12} className="mr-1" /> {req.userPhone || 'Belirtilmemiş'}</span>
                    <span className="flex items-center"><Clock size={12} className="mr-1" /> {new Date(req.createdAt).toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-4 py-1.5 bg-white border border-stone-100 text-amber-800 text-[10px] font-black uppercase tracking-widest rounded-full">
                  {getSubjectLabel(req.subject)}
                </span>
                {req.status === 'open' ? (
                  <button 
                    onClick={() => updateStatus(req.id, 'closed')}
                    className="flex items-center space-x-2 bg-emerald-50 text-emerald-600 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                  >
                    <CheckCircle2 size={16} />
                    <span>Kapat</span>
                  </button>
                ) : (
                  <button 
                    onClick={() => updateStatus(req.id, 'open')}
                    className="flex items-center space-x-2 bg-stone-100 text-stone-600 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-stone-900 hover:text-white transition-all"
                  >
                    <Clock size={16} />
                    <span>Yeniden Aç</span>
                  </button>
                )}
                <button 
                  onClick={() => deleteRequest(req.id)}
                  className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
            
            <div className="bg-stone-50 p-8 rounded-[2rem] border border-stone-100 relative">
              <MessageSquare className="absolute top-6 right-8 text-stone-200" size={48} />
              <p className="text-stone-700 font-medium leading-relaxed italic relative z-10">"{req.message}"</p>
              <div className="mt-4 pt-4 border-t border-stone-100/50 flex items-center justify-between">
                 <p className="text-[10px] text-stone-400 font-mono">USER UID: {req.userId}</p>
                 <div className="flex space-x-4">
                    <a href={`tel:${req.userPhone}`} className="text-[10px] font-black text-amber-800 uppercase tracking-widest hover:underline flex items-center"><Phone size={10} className="mr-1"/> Ara</a>
                    <a href={`mailto:${req.userEmail}`} className="text-[10px] font-black text-amber-800 uppercase tracking-widest hover:underline flex items-center"><Mail size={10} className="mr-1"/> E-posta Gönder</a>
                 </div>
              </div>
            </div>
          </div>
        )) : (
          <div className="py-32 text-center bg-stone-50 rounded-[3rem] border-2 border-dashed border-stone-200">
            <Headphones size={64} className="mx-auto mb-6 text-stone-200" />
            <p className="text-stone-400 font-medium italic">Filtreye uygun destek talebi bulunamadı.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSupport;
